import { Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Form, Formik, FormikErrors } from "formik";
import * as React from "react";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import ErrorMsg from "../../components/ErrorMsg";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import {
  useNewBandobastMutation,
  useUploadLeftLogoMutation,
  useUploadRightLogoMutation,
  useUploadSignatureMutation,
} from "../../services/appln";

interface FormData {
  name: string;
  startDate: string;
  endDate: string;
  place: string;
  heading: string;
  subHeading: string;
  supervisorName: string;
  supervisorMobile: string;
  createTimeslot: boolean;
}

interface Props {
  onSubmitHandler: (newObj: any) => void;
  onCancelHandler: () => void;
  state: boolean;
}

export default function NewBandobast(props: Props) {
  const [createBandobast, createBandobastParams] = useNewBandobastMutation();
  const [uploadLeftLogo, uploadLeftLogoParams] = useUploadLeftLogoMutation();
  const [uploadRightLogo, uploadRightLogoParams] = useUploadRightLogoMutation();
  const [uploadSignature, uploadSignatureParams] = useUploadSignatureMutation();

  const [leftLogo, setLeftLogo] = React.useState<any>("");
  const [rightLogo, setRightLogo] = React.useState<any>("");
  const [signature, setSignature] = React.useState<any>("");
  const [id, setID] = React.useState(undefined);

  const [leftLogoUploaded, setLeftLogoUploaded] =
    React.useState<boolean>(false);
  const [rightLogoUploaded, setRightLogoUploaded] =
    React.useState<boolean>(false);
  const [signatureUploaded, setSignatureUploaded] =
    React.useState<boolean>(false);

  const iv: FormData = {
    name: "",
    startDate: "",
    endDate: "",
    place: "",
    heading: "",
    subHeading: "",
    supervisorName: "",
    supervisorMobile: "",
    createTimeslot: true,
  };

  const onCancelHandlerWrapper = () => {
    createBandobastParams.reset();
    props.onCancelHandler();
  };

  console.log("NewBandobast, createBandobastParams:", createBandobastParams);
  React.useEffect(() => {
    if (createBandobastParams.isSuccess) {
      if (leftLogo === "" && rightLogo === "" && signature === "") {
        props.onSubmitHandler(createBandobastParams.data.id);
      } else {
        setID(createBandobastParams.data.id);
        if (leftLogo !== "") {
          const formData = new FormData();
          formData.append("photo", leftLogo);
          uploadLeftLogo({
            bandobastId: createBandobastParams.data.id,
            formData: formData,
          });
        }
        if (rightLogo !== "") {
          const formData = new FormData();
          formData.append("photo", rightLogo);
          uploadRightLogo({
            bandobastId: createBandobastParams.data.id,
            formData: formData,
          });
        }
        if (signature !== "") {
          const formData = new FormData();
          formData.append("photo", signature);
          uploadSignature({
            bandobastId: createBandobastParams.data.id,
            formData: formData,
          });
        }
      }
    }
  }, [createBandobastParams]);

  React.useEffect(() => {
    if (leftLogo === "") {
      setLeftLogoUploaded(true);
    } else if (uploadLeftLogoParams.isSuccess) {
      setLeftLogoUploaded(true);
    }

    if (rightLogo === "") {
      setRightLogoUploaded(true);
    } else if (uploadRightLogoParams.isSuccess) {
      setRightLogoUploaded(true);
    }

    if (signature === "") {
      setSignatureUploaded(true);
    } else if (uploadRightLogoParams.isSuccess) {
      setSignatureUploaded(true);
    }

    if (leftLogoUploaded && rightLogoUploaded && signatureUploaded) {
      props.onSubmitHandler(id);
    }
  }, [uploadLeftLogoParams, uploadRightLogoParams, uploadSignatureParams]);

  return (
    <div>
      <Dialog
        open={props.state}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {strings.newBandobast}
        </DialogTitle>
        {createBandobastParams.isError && (
          <ErrorMsg msg={strings.failedToCreateBandobast} />
        )}
        {(createBandobastParams.isLoading ||
          uploadLeftLogoParams.isLoading ||
          uploadRightLogoParams.isLoading ||
          uploadSignatureParams.isLoading) && <ProgressIndicator />}
        <DialogContent>
          <Formik
            initialValues={iv}
            validate={(values) => {
              const errors: FormikErrors<FormData> = {};
              if (!values.name) {
                errors.name = strings.nameMandatory;
              }
              if (!values.supervisorName) {
                errors.supervisorName = strings.supervisorNameMandatory;
              }
              if (!values.supervisorMobile) {
                errors.supervisorMobile = strings.supervisorMobileMandatory;
              }
              if (!values.startDate) {
                errors.startDate = strings.startDateMandatory;
              }
              if (!values.endDate) {
                errors.endDate = strings.endDateMandatory;
              }

              if (values.startDate && values.endDate) {
                const startDateObj = Date.parse(values.startDate);
                const endDateObj = Date.parse(values.endDate);
                if (startDateObj > endDateObj) {
                  errors.startDate = strings.startDateLaterThanEndDate;
                }
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              createBandobast(values);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="name"
                        name="name"
                        label={strings.name}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="startDate"
                        type="date"
                        name="startDate"
                        label={strings.startDate}
                        value={values.startDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.startDate && Boolean(errors.startDate)}
                        helperText={touched.startDate && errors.startDate}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="endDate"
                        type="date"
                        name="endDate"
                        label={strings.endDate}
                        value={values.endDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.endDate && Boolean(errors.endDate)}
                        helperText={touched.endDate && errors.endDate}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="supervisorName"
                        name="supervisorName"
                        label={strings.supervisorName}
                        value={values.supervisorName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.supervisorName &&
                          Boolean(errors.supervisorName)
                        }
                        helperText={
                          touched.supervisorName && errors.supervisorName
                        }
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="supervisorMobile"
                        name="supervisorMobile"
                        label={strings.supervisorMobile}
                        value={values.supervisorMobile}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.supervisorMobile &&
                          Boolean(errors.supervisorMobile)
                        }
                        helperText={
                          touched.supervisorMobile && errors.supervisorMobile
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="heading"
                        name="heading"
                        label={strings.heading}
                        value={values.heading}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.heading && Boolean(errors.heading)}
                        helperText={touched.heading && errors.heading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="subHeading"
                        name="subHeading"
                        label={strings.subHeading}
                        value={values.subHeading}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.subHeading && Boolean(errors.subHeading)}
                        helperText={touched.subHeading && errors.subHeading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="place"
                        name="place"
                        label={strings.place}
                        value={values.place}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.place && Boolean(errors.place)}
                        helperText={touched.place && errors.place}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormGroup row={true}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.createTimeslot}
                              inputProps={{ "aria-label": "controlled" }}
                            />
                          }
                          id="createTimeslot"
                          name="createTimeslot"
                          onChange={handleChange}
                          label={strings.createDefaultTimeslot}
                        />
                      </FormGroup>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={"row"} spacing={1}>
                        <div>{strings.leftLogo}</div>
                        <input
                          id="photo"
                          name="photo"
                          type="file"
                          onChange={(event) => {
                            if (event && event.target) {
                              const target = event.target as HTMLInputElement;
                              if (target.files) {
                                const file = target.files[0];
                                setLeftLogo(file);
                              }
                            }
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={"row"} spacing={1}>
                        <div>{strings.rightLogo}</div>
                        <input
                          id="photo"
                          name="photo"
                          type="file"
                          onChange={(event) => {
                            if (event && event.target) {
                              const target = event.target as HTMLInputElement;
                              if (target.files) {
                                const file = target.files[0];
                                setRightLogo(file);
                              }
                            }
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={"row"} spacing={1}>
                        <div>{strings.signature}</div>
                        <input
                          id="photo"
                          name="photo"
                          type="file"
                          onChange={(event) => {
                            if (event && event.target) {
                              const target = event.target as HTMLInputElement;
                              if (target.files) {
                                const file = target.files[0];
                                setSignature(file);
                              }
                            }
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {strings.create}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={onCancelHandlerWrapper}
                        >
                          {strings.cancel}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
